<!--@format-->
<template>
    <div v-cloak ref="board" class="board" :class="componentClasses">
        <board-title-bar :title="title" :type="type">
            <slot slot="infoCard" name="infoCard" />
            <slot slot="titleActions" name="titleActions" />
            <slot slot="titleIcon" name="titleIcon" />
        </board-title-bar>
        <board-view-bar
            v-if="showViewBar"
            :currentView="currentView"
            :type="type"
            :views="views"
            :filterable="filterable"
            @update:views="updateViews"
            @update:view="changeView"
        />
        <board-view
            v-if="currentView"
            ref="boardView"
            :type="type"
            :loadFailed="loadFailed"
            :view="currentView"
            :displays="displays"
            :page="currentPage"
            :search="search"
            :loading="loading || loadingViews || changingView"
            :searchable="searchable"
            :search-placeholder="searchPlaceholder"
            :selectable="selectable"
            :filterable="filterable"
            :noRecordsContent="noRecordsContent"
            :sticky="sticky"
            :items="computedItems"
            :selectedItems="value"
            :totalItems="totalItems"
            :pagination="pagination"
            :headers="headers"
            :itemActions="itemActions"
            :bulkActions="bulkActions"
            :refreshEvents="refreshEvents"
            :resetEvents="resetEvents"
            :customViews="customViews"
            :quick-filters-active="quickFiltersActive"
            :filter-options="filterOptions"
            :is-draggable="isDraggable"
            @remove:chip="removeChip"
            @update:view="updateCurrentView"
            @update:view-display="updateCurrentViewDisplay"
            @update:search="updateSearch"
            @update:page="updatePage"
            @update:selectedItems="updateSelectedItems"
            @update:custom-field-value="updateCustomFieldValue"
            @copy:view="copyView"
            @query="emitQuery"
            @selectAll="selectAll"
        >
            <slot slot="boardCard" name="boardCard" />
            <slot slot="moreActions" name="moreActions" />
            <slot slot="noRecords" name="noRecords" />
            <slot name="customDropdown" slot="customDropdown" />
            <slot name="customAction" slot="customAction" />
            <slot slot="customFilters" name="customFilters" />
        </board-view>
    </div>
</template>

<script>
const BoardTitleBar = () => import('@/components/board/board-title-bar');
const BoardViewBar = () => import('@/components/board/board-view-bar');
const BoardView = () => import('@/components/board/board-view');

import EventBus from '@/scripts/event-bus.js';
import { debounce } from 'lodash';
import { ResponsiveMixin } from 'vue-responsive-components';
import { responsiveHelpers } from '@/scripts/helpers';

export default {
    components: {
        'board-title-bar': BoardTitleBar,
        'board-view-bar': BoardViewBar,
        'board-view': BoardView,
    },

    mixins: [ResponsiveMixin],

    props: {
        refreshEvents: {
            type: Array,
            default: () => [],
        },

        resetEvents: {
            type: Array,
            default: () => [],
        },

        type: {
            type: String,
            required: true,
        },

        headers: {
            type: Array,
            required: true,
        },

        items: {
            type: Array,
            required: true,
        },

        deletingItems: {
            type: Array,
            default: () => [],
        },

        processingItems: {
            type: Array,
            default: () => [],
        },

        totalItems: {
            type: Number,
            required: true,
        },

        value: {
            type: Array,
            default: () => [],
        },

        loading: {
            type: Boolean,
            default: false,
        },

        selectable: {
            type: Boolean,
            default: false,
        },

        searchable: {
            type: Boolean,
            default: false,
        },

        searchPlaceholder: {
            type: String,
            default: 'Search',
        },

        filterable: {
            type: Boolean,
            default: false,
        },

        title: {
            type: String,
            default: '',
        },

        customViews: {
            type: Boolean,
            default: false,
        },

        sticky: {
            type: Boolean,
            default: false,
        },

        pagination: {
            type: Boolean,
            default: true,
        },

        itemActions: {
            type: Array,
            default: () => [],
        },

        bulkActions: {
            type: Array,
            default: () => [],
        },

        noRecordsContent: {
            type: String,
            default: null,
        },

        viewQueryParam: {
            type: String,
            default: null,
        },

        pageQueryParam: {
            type: String,
            default: null,
        },

        searchQueryParam: {
            type: String,
            default: null,
        },

        displays: {
            type: Array,
            default: () => ['table'],
        },

        quickFiltersActive: {
            type: Boolean,
            default: false,
        },

        defaultViews: {
            type: Array,
            default: null,
        },

        filterOptions: {
            type: Boolean,
            default: true,
        },

        isDraggable: {
            type: Boolean,
            default: true,
        },

        loadFailed: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        var initialPage = this.getPageFromUrl() || 1;
        var initialSearch = this.getSearchFromUrl() || '';
        return {
            loadingViews: true,
            changingView: false,
            currentView: null,
            search: initialSearch,
            currentPage: initialPage,
            views: [],
            lastQuery: {
                viewId: null,
                search: initialSearch,
                page: initialPage,
            },

            emitQuery: debounce((options) => {
                this.updateUrlHistory(this.currentView.id, options.page, options.search);
                this.$emit('query', options);
            }, 500),
        };
    },

    computed: {
        componentClasses() {
            return [responsiveHelpers.getBreakpointClasses(this)];
        },

        computedItems() {
            return this.items.map((item) => {
                return {
                    data: item,
                    selected: this.isSelected(item),
                    deleting: this.isDeleting(item),
                    processing: this.isProcessing(item),
                };
            });
        },

        showViewBar() {
            return this.customViews && !this.loadingViews;
        },

        userId() {
            return this.$store.state.user.id;
        },
    },

    watch: {
        loadingViews() {
            if (!this.loadingViews) {
                this.loadInitialView();
            }
        },

        items() {
            this.updateUiPositioning();
            this.changingView = false;
        },

        $route() {
            if (this.views.length > 0 && this.currentView) {
                var viewId = this.getViewIdFromUrl() || this.currentView.id;
                var matchingView = this.views.find((v) => v.id === viewId);
                this.currentView = matchingView || this.views[0];
                this.currentPage = this.getPageFromUrl() || 1;
                this.search = this.getSearchFromUrl() || '';
            }
        },

        currentView(view) {
            this.$emit('update:currentview', view);
        },
    },

    breakpoints: responsiveHelpers.getBreakpoints(),

    mounted() {
        EventBus.$on('view:update', this.getViews);
        this.getViews();
    },

    beforeDestroy() {
        EventBus.$off('view:update', this.getViews);
    },

    methods: {
        updatePage(page) {
            this.currentPage = page;
        },

        updateSearch(search) {
            this.search = search;
            this.currentPage = 1;
        },

        removeChip(chip) {
            this.$emit('remove:chip', chip);
        },

        getViewIdFromUrl() {
            var viewId = null;

            if (this.viewQueryParam && this.$route.query[this.viewQueryParam]) {
                viewId = parseInt(this.$route.query[this.viewQueryParam]);
            }

            return viewId;
        },

        getPageFromUrl() {
            var page = 0;

            if (this.pageQueryParam && this.$route.query[this.pageQueryParam]) {
                page = parseInt(this.$route.query[this.pageQueryParam]);
            }

            return page;
        },

        getSearchFromUrl() {
            var search = '';

            if (this.searchQueryParam && this.$route.query[this.searchQueryParam]) {
                search = this.$route.query[this.searchQueryParam];
            }

            return search;
        },

        updateUrlHistory(viewId, page, search) {
            var viewChanged = this.lastQuery.viewId !== viewId;
            var pageChanged = this.lastQuery.page !== page;
            var searchChanged = this.lastQuery.search !== search;

            var shouldUpdateHistory = viewChanged || pageChanged || searchChanged;
            if (!shouldUpdateHistory) return;

            this.lastQuery = {
                viewId,
                search,
                page,
            };

            var params = [];

            if (this.viewQueryParam && viewId) {
                params.push(`${this.viewQueryParam}=${viewId}`);
            }

            if (this.pageQueryParam && page) {
                params.push(`${this.pageQueryParam}=${page}`);
            }

            if (this.searchQueryParam && search) {
                var encodedSearch = encodeURIComponent(search);
                params.push(`${this.searchQueryParam}=${encodedSearch}`);
            }

            for (var key of Object.keys(this.$route.query)) {
                if (
                    key !== this.viewQueryParam &&
                    key !== this.pageQueryParam &&
                    key !== this.searchQueryParam
                ) {
                    params.push(`${key}=${this.$route.query[key]}`);
                }
            }

            var routeHash = this.$route.hash || '';
            var newUrl = `${this.$route.path}?${params.join('&')}${routeHash}`;
            this.$router.push(newUrl).catch(() => {});
        },

        updateUiPositioning() {
            this.$nextTick(() => {
                window.dispatchEvent(new Event('resize'));
            });
        },

        copyView() {
            var newViewIndex = Math.min(this.views.indexOf(this.currentView) + 1, 3);

            var newView = JSON.parse(JSON.stringify(this.currentView));
            newView.id = null;
            newView.name = `${newView.name} (Copy)`;

            var newViews = [...this.views];
            newViews.splice(newViewIndex, 0, newView);

            this.updateViews(newViews);
            this.changeView(newView);
        },

        changeView(newView) {
            this.changingView = true;
            this.search = '';
            this.currentPage = 1;
            this.currentView = newView;
        },

        clearSelectedItems() {
            this.updateSelectedItems([]);
        },

        getViews() {
            this.clearSelectedItems();

            this.loadingViews = true;
            this.currentView = null;

            this.$api
                .get(
                    `/service/Views?$select=Id,Name,Config&$filter=UserId eq '${this.userId}' and Type eq '${this.type}'`,
                )
                .then((response) => {
                    this.views = response.data.value.map((view) => {
                        var config = JSON.parse(view.Config);

                        var headers = this.parseConfigHeaders(config.headers);
                        var sort = this.parseConfigSort(config.sort, headers);
                        return {
                            id: view.Id,
                            name: view.Name || 'View',
                            icon: config.icon || '',
                            display: config.display || this.displays[0] || 'table',
                            itemsPerPage: config.itemsPerPage || 10,
                            sort: sort,
                            headers: headers,
                            filters: config.filters || {
                                operation: 'And',
                                value: [],
                            },
                            quickFilters:
                                this.quickFiltersActive && config.quickFilters
                                    ? config.quickFilters
                                    : {
                                          operation: 'And',
                                          active: this.quickFiltersActive,
                                          activeFilters: [],
                                      },
                        };
                    });
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to fetch views.', {
                        error,
                    });
                })
                .finally(() => {
                    this.loadingViews = false;
                });
        },

        parseConfigHeaders(configHeaders) {
            if (!this.areValidConfigHeaders(configHeaders)) {
                return this.buildDefaultViewHeaders();
            }

            var allowedConfigHeaders = configHeaders.filter((c) =>
                this.headers.some((h) => h.key === c.key),
            );

            for (var i = 0; i < this.headers.length; i++) {
                var header = this.headers[i];
                var hasMatchingConfig = allowedConfigHeaders.some((c) => c.key === header.key);
                if (!hasMatchingConfig) {
                    allowedConfigHeaders.push({
                        key: header.key,
                        pinned: false,
                        visible: true,
                    });
                }
            }

            return allowedConfigHeaders;
        },

        areValidConfigHeaders(configHeaders) {
            return (
                configHeaders &&
                configHeaders.length > 0 &&
                configHeaders.every((h) => {
                    return (
                        h &&
                        h.key !== undefined &&
                        h.pinned !== undefined &&
                        h.visible !== undefined
                    );
                })
            );
        },

        parseConfigSort(configSort, headers) {
            if (!configSort) {
                return null;
            }

            const hasMatchingKey = headers.some((h) => h.key === configSort.key);
            return hasMatchingKey ? configSort : null;
        },

        selectAll(options) {
            this.$emit('selectAll', options);
        },

        updateSelectedItems(selectedItems) {
            this.$emit('input', selectedItems);
        },

        updateCurrentView(view) {
            this.currentPage = 1;
            this.currentView = view;
            this.updateView(view);
        },

        updateCurrentViewDisplay(display) {
            this.currentView.display = display;
            this.updateView(this.currentView);
        },

        isSelected(item) {
            return this.value.some((s) => s.id === item.id);
        },

        isDeleting(item) {
            return this.deletingItems.some((s) => s.id === item.id);
        },

        isProcessing(item) {
            return this.processingItems.some((s) => s.id === item.id);
        },

        loadInitialView() {
            if (!this.currentView) {
                if (this.views.length === 0) {
                    this.createDefaultView();
                } else if (this.viewQueryParam) {
                    var viewId = this.getViewIdFromUrl();
                    var matchingView = this.views.find((v) => v.id === viewId);
                    this.currentView = matchingView || this.views[0];
                } else {
                    this.currentView = this.views[0];
                }
            }
        },

        updateView(view) {
            this.views = this.views.map((v) => {
                return v.id === view.id ? view : v;
            });
        },

        updateViews(views) {
            this.views = [...views];
        },

        updateCustomFieldValue(event) {
            this.$emit('update:custom-field-value', event);
        },

        createDefaultView() {
            if (!this.defaultViews) {
                this.currentView = {
                    id: 0,
                    name: 'Main',
                    display: this.displays[0] || 'table',
                    headers: this.buildDefaultViewHeaders(),
                    icon: 'home',
                    itemsPerPage: 10,
                    sort: null,
                    filters: {
                        operation: 'And',
                        value: [],
                    },

                    quickFilters: {
                        operation: 'And',
                        active: this.quickFiltersActive,
                        activeFilters: [],
                        filtersApplied: '',
                    },
                };
                this.views = [this.currentView];
            } else {
                this.defaultViews.forEach((view) => {
                    view.headers = this.buildDefaultViewHeaders();
                });

                this.views = this.defaultViews;
                this.currentView = this.views[0];
            }
        },

        buildDefaultViewHeaders() {
            return this.headers.map((h) => {
                return {
                    key: h.key,
                    pinned: false,
                    visible: true,
                };
            });
        },
    },
};
</script>

<style>
.board {
    position: relative;
    color: var(--color-text-default);
}
</style>
